import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { environment } from '../../environments/environment';
import { catchError, map, switchMap } from 'rxjs/operators';
import { TokenService } from './token.service';
import { MessageService } from './message.service';
import { CreateUserCropSelectionDto, UserCropSelection } from '../models/user-crop-selection.model';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private readonly apiUrl = environment.apiUrl + '/users';
  private readonly cropSelectionsUrl = environment.apiUrl + '/user-crop-selections';
  private readonly http = inject(HttpClient);
  private readonly tokenService = inject(TokenService);
  private readonly messageService = inject(MessageService);

  getUserInfo(): Observable<Record<string, unknown>> {
    // Backend exposes /api/users/profile, not /api/users/info
    return this.getAuthenticatedRequest<Record<string, unknown>>(`${this.apiUrl}/profile`);
  }

  getUserCrops(): Observable<Record<string, unknown>[]> {
    // Handle 404 from /users/crops as "no crops yet" rather than a hard error
    return this.getAuthenticatedRequest<Record<string, unknown>[]>(`${this.apiUrl}/crops`).pipe(
      catchError(error => {
        if (error && error.status === 404) {
          console.log('No user crops found (404). Treating as empty crop list.');
          return of([]);
        }
        throw error;
      })
    );
  }

  updateUserInfo(userInfo: Record<string, unknown>): Observable<Record<string, unknown>> {
    return this.tokenService.getAccessToken().pipe(
      switchMap(token => {
        if (!token) {
          throw new Error('No access token found');
        }

        const headers = new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        });

        // Backend also uses /profile for updating
        return this.http.put<Record<string, unknown>>(`${this.apiUrl}/profile`, userInfo, { headers });
      }),
      catchError(error => {
        console.error('Error updating user info:', error);
        throw error;
      })
    );
  }

  updateUserPreferences(preferences: Record<string, unknown>): Observable<Record<string, unknown>> {
    return this.tokenService.getAccessToken().pipe(
      switchMap(token => {
        if (!token) {
          throw new Error('No access token found');
        }

        const headers = new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        });

        return this.http.put<Record<string, unknown>>(`${this.apiUrl}/preferences`, preferences, { headers });
      }),
      catchError(error => {
        console.error('Error updating preferences:', error);
        throw error;
      })
    );
  }

  /**
   * Save a crop selection for the current user
   * NOTE: This creates a user-crop-selection (favorite), NOT a catalog crop
   * @param cropSelection The crop selection data
   */
  saveCropSelection(cropSelection: CreateUserCropSelectionDto): Observable<UserCropSelection> {
    return this.tokenService.getAccessToken().pipe(
      switchMap(token => {
        if (!token) {
          this.messageService.error('Authentication required', 'Save Failed');
          throw new Error('No access token found');
        }

        const headers = new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        });

        return this.http.post<UserCropSelection>(this.cropSelectionsUrl, cropSelection, { headers });
      }),
      catchError(error => {
        console.error('Error saving crop selection:', error);
        const errorMessage = error.error?.message || 'Failed to save crop selection';
        this.messageService.error(errorMessage, 'Save Failed');
        throw error;
      })
    );
  }

  /**
   * Gets all crop selections for the current user
   */
  getUserCropSelections(): Observable<UserCropSelection[]> {
    return this.getAuthenticatedRequest<UserCropSelection[]>(this.cropSelectionsUrl).pipe(
      catchError(error => {
        // Only show error messages for non-404 errors (404 is expected for new users)
        if (error.status !== 404) {
          const errorMessage = error.error?.message || 'Failed to load crop selections';
          this.messageService.error(errorMessage, 'Load Failed');
        }
        throw error;
      })
    );
  }

  /**
   * Get the user profile from the API
   */
  getUserProfile(): Observable<Record<string, unknown>> {
    return this.getAuthenticatedRequest<Record<string, unknown>>(`${this.apiUrl}/profile`).pipe(
      catchError(error => {
        const errorMessage = error.error?.message || 'Failed to load user profile';
        this.messageService.error(errorMessage, 'Profile Error');
        throw error;
      })
    );
  }

  /**
   * Check if the user is new (has no saved crop selections)
   */
  isNewUser(): Observable<boolean> {
    return this.getUserCropSelections().pipe(
      map(cropSelections => {
        // If we get a successful response, check if it's empty
        return !cropSelections || cropSelections.length === 0;
      }),
      catchError(error => {
        console.log('Error checking crop selections, treating as new user:', error);

        // Handle specific HTTP error codes
        if (error.status === 404 || error.status === 401) {
          // 404 or 401 means user has no data or auth issues - treat as new user
          return of(true);
        }

        // For other errors, also assume new user to be safe
        return of(true);
      })
    );
  }

  /**
   * Helper method to make authenticated requests
   */
  private getAuthenticatedRequest<T>(url: string): Observable<T> {
    return this.tokenService.getAccessToken().pipe(
      switchMap(token => {
        if (!token) {
          this.messageService.error('Authentication required. Please sign in.', 'Auth Error');
          // Emit a structured auth error instead of a generic Error
          return of(null).pipe(
            switchMap(() => {
              throw { type: 'auth', message: 'No access token found' };
            })
          );
        }

        const headers = new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        });

        return this.http.get<T>(url, { headers });
      }),
      catchError(error => {
        console.error(`Error fetching from ${url}:`, error);

        // Distinguish between HTTP errors, custom auth errors, and unknown errors
        if (error && typeof error === 'object' && 'status' in error) {
          const status = (error as { status?: number }).status ?? 0;

          if (status === 0) {
            this.messageService.error(
              'Unable to reach the server. Please check your connection or try again later.',
              'Network Error'
            );
          } else if (status === 401 || status === 403) {
            this.messageService.error(
              'Your session has expired or you are not authorized. Please sign in again.',
              'Auth Error'
            );
          } else {
            const message = (error as { error?: { message?: string } }).error?.message ||
              'An unexpected error occurred while contacting the server.';
            this.messageService.error(message, 'Request Error');
          }
        } else if (error && (error as { type?: string }).type === 'auth') {
          this.messageService.error('Authentication required. Please sign in.', 'Auth Error');
        } else {
          this.messageService.error('An unexpected error occurred.', 'Error');
        }

        throw error;
      })
    );
  }
}
